import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LockClosedIcon, EyeIcon, EyeSlashIcon, UserIcon, EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";

const AgentSignup = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Email Verification State
    const [emailOtp, setEmailOtp] = useState("");
    const [showEmailOtpInput, setShowEmailOtpInput] = useState(false);
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);

    const { agentSignup } = useAuthStore();
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Reset email verification if email changes
        if (name === "email" && emailVerified) {
            setEmailVerified(false);
            setShowEmailOtpInput(false);
            setEmailOtp("");
        }
    };

    const sendEmailOtp = async () => {
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setIsVerifyingEmail(true);
        try {
            const response = await axiosInstancev1.post(
                "/real-estate-agents/auth/send-email-otp",
                { email: formData.email }
            );
            if (response.data.success) {
                setShowEmailOtpInput(true);
                toast.success(response.data.message || "OTP sent successfully!");
            } else {
                toast.error(response.data.message || "Failed to send OTP.");
            }
        } catch (error) {
            const errorMsg =
                error.response?.data?.message ||
                "An account with this email already exists or failed to send OTP.";
            toast.error(errorMsg);
        } finally {
            setIsVerifyingEmail(false);
        }
    };

    const verifyEmailOtp = async () => {
        if (!emailOtp || emailOtp.length !== 6) {
            toast.error("Please enter the 6-digit OTP.");
            return;
        }
        setIsVerifyingEmail(true); // Reuse loading state
        try {
            const response = await axiosInstancev1.post(
                "/real-estate-agents/auth/verify-email-otp",
                { email: formData.email, otp: emailOtp }
            );
            if (response.data.success) {
                toast.success(response.data.message || "Email verified successfully!");
                setEmailVerified(true);
                setShowEmailOtpInput(false);
            } else {
                toast.error(response.data.message || "Invalid OTP.");
            }
        } catch (error) {
            const errorMsg =
                error.response?.data?.message || "OTP verification failed.";
            toast.error(errorMsg);
        } finally {
            setIsVerifyingEmail(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        if (!emailVerified) {
            toast.error("Please verify your email before signing up.");
            return;
        }
        setLoading(true);
        try {
            const success = await agentSignup(formData);
            if (success) {
                navigate("/agent/login");
            }
        } catch (error) {
            console.error("Agent signup failed:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
            style={{
                backgroundImage: "url('/assets/CustomerBg.jpg')",
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
            }}
        >
            {/* Black overlay */}
            <div className="absolute inset-0 bg-black opacity-50 z-0"></div>
            <div
                className="flex flex-col w-full max-w-sm p-8 border border-gray-200 bg-white/90 rounded-2xl shadow-2xl overflow-hidden items-center backdrop-blur-md z-10 relative"
                style={{ boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)' }}
            >
                <div className="flex flex-col justify-center w-full p-4 lg:p-6 text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2 tracking-tight uppercase">
                        Agent SIGNUP
                    </h2>
                    <form className="mt-2 w-full" onSubmit={handleSignup}>
                        {/* Name Input */}
                        <div className="mb-3">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <UserIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm"
                                    style={{ backgroundColor: "#f3f4f6" }}
                                    placeholder="Full Name"
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        {/* Email Input */}
                        <div className="mb-3">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <EnvelopeIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`w-full pl-10 pr-24 py-2 border ${emailVerified ? "border-green-500 bg-green-50" : "border-gray-300 bg-gray-100"
                                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm`}
                                    style={{ backgroundColor: emailVerified ? "#f0fdf4" : "#f3f4f6" }}
                                    placeholder="Email ID"
                                    required
                                    disabled={loading || emailVerified}
                                />
                                {!emailVerified && (
                                    <button
                                        type="button"
                                        onClick={sendEmailOtp}
                                        disabled={isVerifyingEmail || !formData.email}
                                        className="absolute right-1 top-1 bottom-1 px-3 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                                    >
                                        {isVerifyingEmail ? (
                                            <AiOutlineLoading3Quarters className="animate-spin" />
                                        ) : (
                                            "Verify"
                                        )}
                                    </button>
                                )}
                                {emailVerified && (
                                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-600 text-xs font-bold">
                                        Verified
                                    </span>
                                )}
                            </div>

                            {/* OTP Input */}
                            {showEmailOtpInput && (
                                <div className="mt-2 flex gap-2">
                                    <input
                                        type="text"
                                        value={emailOtp}
                                        onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        placeholder="Enter 6-digit OTP"
                                    />
                                    <button
                                        type="button"
                                        onClick={verifyEmailOtp}
                                        disabled={isVerifyingEmail || emailOtp.length !== 6}
                                        className="px-4 py-2 bg-green-600 text-white text-sm rounded-xl hover:bg-green-700 disabled:bg-gray-400 font-medium"
                                    >
                                        {isVerifyingEmail ? (
                                            <AiOutlineLoading3Quarters className="animate-spin" />
                                        ) : (
                                            "Submit"
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Phone Input */}
                        <div className="mb-3">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <PhoneIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm font-poppins"
                                    style={{ backgroundColor: "#f3f4f6" }}
                                    placeholder="Phone Number"
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className="mb-3">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                    <LockClosedIcon className="w-5 h-5 text-black" />
                                </span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm"
                                    style={{ backgroundColor: "#f3f4f6" }}
                                    placeholder="Password"
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="h-5 w-5" />
                                    ) : (
                                        <EyeIcon className="h-5 w-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-28 px-4 py-1 mt-4 text-white transition-all duration-300 flex items-center justify-center rounded-xl text-sm font-semibold shadow-sm ${loading ? "opacity-70 cursor-not-allowed bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
                                    }`}
                            >
                                {loading ? (
                                    <AiOutlineLoading3Quarters className="animate-spin text-lg" />
                                ) : (
                                    "SIGNUP"
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Already have an account? Sign in */}
                    <div className="mt-6 text-xs text-gray-700">
                        <span>Already have an account?</span>
                        <button
                            onClick={() => navigate("/agent/login")}
                            className="ml-1 font-semibold text-gray-900 hover:underline"
                            style={{ color: "#555555" }}
                            disabled={loading}
                        >
                            Log in
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentSignup;
